/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 * @param {File} imageSrc - Image File url
 * @param {Object} pixelCrop - pixelCrop Object provided by react-easy-crop
 * @param {number} rotation - optional rotation parameter
 */
import pica from "pica";

// const picaInstance = pica();
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  // set each dimensions to double largest dimension to allow for a safe area for the
  // image to rotate in without being clipped by canvas context
  canvas.width = safeArea;
  canvas.height = safeArea;

  // translate canvas context to a central location on image to allow rotating around the center.
  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.translate(-safeArea / 2, -safeArea / 2);

  // draw rotated image and store data.
  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  );

  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  // set canvas width to final desired crop size - this will clear existing context
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // paste generated rotate image with correct offsets for x,y crop values.
  ctx.putImageData(
    data,
    0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x,
    0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y
  );

  // As Base64 string
  // return canvas.toDataURL("image/jpeg");
  return canvas;
}

const generateDownload = async (src, ext, crop, upload = false) => {
  if (!crop || !src) {
    return;
  }

  const canvas = await getCroppedImg(src, crop);
  if (upload) {
    const mimeType = ext === "png" ? "image/png" : "image/jpeg";
    const base64 = canvas.toDataURL(mimeType);
    return base64;
  }
  canvas.toBlob(
    (blob) => {
      const previewUrl = window.URL.createObjectURL(blob);

      const anchor = document.createElement("a");
      anchor.download = `croppedImage.${ext}`;
      anchor.href = URL.createObjectURL(blob);
      anchor.click();

      window.URL.revokeObjectURL(previewUrl);
    },
    "image/jpeg",
    0.66
  );
};

// const resizeImageToCropSize = async (imageSrc, cropSize) => {
//   const img = new Image();
//   img.crossOrigin = "anonymous";
//   img.src = imageSrc;

//   await new Promise((resolve) => {
//     img.onload = resolve;
//   });

//   const offScreenCanvas = document.createElement("canvas");
//   offScreenCanvas.width = img.width;
//   offScreenCanvas.height = img.height;
//   const ctx = offScreenCanvas.getContext("2d");
//   ctx.drawImage(img, 0, 0);

//   const targetCanvas = document.createElement("canvas");
//   targetCanvas.width = cropSize.width;
//   targetCanvas.height = cropSize.height;

//   await picaInstance.resize(offScreenCanvas, targetCanvas, {
//     quality: 3, // max
//     unsharpAmount: 80,
//     unsharpThreshold: 2,
//   });

//   const resizedImage = await picaInstance.toBlob(targetCanvas, "image/png");
//   return URL.createObjectURL(resizedImage);
// };

const resizeImageToCropSize = async (imageSrc, cropSize) => {
  const img = new Image();
  img.src = imageSrc;

  // Try to use crossOrigin only if not same-origin
  if (!imageSrc.startsWith(window.location.origin)) {
    img.crossOrigin = "anonymous";
  }

  await new Promise((resolve) => {
    img.onload = resolve;
  });

  // Try to use Pica safely
  try {
    const offScreenCanvas = document.createElement("canvas");
    offScreenCanvas.width = img.width;
    offScreenCanvas.height = img.height;
    const ctx = offScreenCanvas.getContext("2d");
    ctx.drawImage(img, 0, 0);

    const targetCanvas = document.createElement("canvas");
    targetCanvas.width = cropSize.width;
    targetCanvas.height = cropSize.height;

    // Try using Pica, may fail in Brave
    await pica().resize(offScreenCanvas, targetCanvas, {
      quality: 3,
      unsharpAmount: 80,
      unsharpThreshold: 2,
    });

    const resizedBlob = await pica().toBlob(targetCanvas, "image/png");
    return URL.createObjectURL(resizedBlob);
  } catch (error) {
    console.warn("Pica failed, falling back to drawImage:", error.message);

    // Fallback to simple canvas resizing
    const canvas = document.createElement("canvas");
    canvas.width = cropSize.width;
    canvas.height = cropSize.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/png")
    );

    return URL.createObjectURL(blob);
  }
};

export { generateDownload, resizeImageToCropSize };
